import type {
  CabinClass,
  SearchLegRequest,
  SearchRequest,
  SuggestionContext,
  SuggestionItem,
  SuggestionItemType,
  TripType,
  WidenKeys,
} from '@optionway/models';
import {
  CabinClassEnum,
  modelsImpl,
  PARAM_ADULTS,
  PARAM_CABIN_CLASS,
  PARAM_CHILDREN,
  PARAM_INFANTS,
  PARAM_LEGS_DATE,
  PARAM_LEGS_FROM_CODE,
  PARAM_LEGS_FROM_TYPE,
  PARAM_LEGS_TO_CODE,
  PARAM_LEGS_TO_TYPE,
  PARAM_TRIP_TYPE,
  SuggestionItemTypeEnum,
  TripTypeEnum,
} from '@optionway/models';
import { DateTime } from 'luxon';
import { toCacheKey } from '@pinia/colada';

type MainCriteriaKey = Omit<SearchRequest, 'legs' | 'sessionData'> & {
  [K in keyof Pick<SearchRequest, 'legs'>]: Pick<SearchRequest[K][number], 'from' | 'to' | 'departureDate'>[];
};

interface SearchCriteriaStore {
  searchRequest: SearchRequest;
  // One-shot search trigger — controls query enabled state.
  // false (idle)  — query disabled; criteria changes do not auto-fire a fetch.
  // true  (armed) — query fires once; list.vue resets it to false on settle.
  active: boolean;
  // Set by entry-point middleware when criteria were pre-filled from URL query params.
  // form.vue onMounted reads it, resets it, then triggers search — SSR-friendly alternative to history.state.
  fromDeepLink: boolean;
  // Snapshot of the criteria key at the moment the user triggered a search — read by both search
  // queries (via committedCriteriaKey) and by search-result-index, so that editing the form after a
  // search shifts neither the query key nor the cache lookup.
  searchedCriteriaKey?: MainCriteriaKey;
  // Set by the offer-expiration dialog to ask list.vue (the query owner) to re-run the committed
  // search; the dialog itself never touches the query. Consumed and reset in list.vue.
  refreshRequested: boolean;
}

const SEARCH_REQUEST_DEFAULTS: Omit<SearchRequest, 'legs' | 'sessionData'> = {
  tripType: 'ROUND_TRIP',
  cabinClass: 'ECONOMY',
  adults: 1,
  children: 0,
  infants: 0,
};

export const MAX_LEGS = 6;
const MAX_PAX = 9;

export const useSearchCriteriaStore = defineStore('search-criteria', {
  state: (): SearchCriteriaStore => {
    const initSearch = new modelsImpl.SearchRequestImpl();
    initSearch.tripType = SEARCH_REQUEST_DEFAULTS.tripType;
    initSearch.cabinClass = SEARCH_REQUEST_DEFAULTS.cabinClass;
    initSearch.adults = SEARCH_REQUEST_DEFAULTS.adults;
    initSearch.children = SEARCH_REQUEST_DEFAULTS.children;
    initSearch.infants = SEARCH_REQUEST_DEFAULTS.infants;
    initSearch.legs.push(newLeg(), newLeg());
    return {
      searchRequest: toPojo(initSearch),
      active: false,
      fromDeepLink: false,
      searchedCriteriaKey: undefined,
      refreshRequested: false,
    };
  },
  getters: {
    tripTypeList: (): TripType[] => [TripTypeEnum.ROUND_TRIP, TripTypeEnum.ONE_WAY, TripTypeEnum.MULTI_DEST],
    cabinClassList: (): CabinClass[] => Object.values(CabinClassEnum),
    suggestionItemTypeList: (): SuggestionItemType[] => Object.values(SuggestionItemTypeEnum),

    toBody: (state) => {
      return () => {
        const searchObject = new modelsImpl.SearchRequestImpl() as WidenKeys<SearchRequest>;
        for (const key of Object.keys(searchObject) as (keyof SearchRequest)[]) {
          searchObject[key] = toRaw(state.searchRequest[key]);
        }
        const body = toPojo(searchObject);
        return body;
      };
    },

    isOneWay: (state) => state.searchRequest.tripType === 'ONE_WAY',
    committedIsOneWay(): boolean {
      return this.committedCriteriaKey.tripType === 'ONE_WAY';
    },
    isRoundTrip: (state) => state.searchRequest.tripType === 'ROUND_TRIP',
    isMultiDestination: (state) => state.searchRequest.tripType === 'MULTI_DEST',
    hasMaxLegs: (state) => state.searchRequest.legs.length >= MAX_LEGS,
    committedIsMultiDestination(): boolean {
      return this.committedCriteriaKey.tripType === 'MULTI_DEST';
    },

    departureDate: (state) => state.searchRequest.legs[0]?.departureDate,
    committedDepartureDate(): string | undefined {
      return this.committedCriteriaKey.legs[0]?.departureDate;
    },
    lastFlightDate: (state) => state.searchRequest.legs.at(-1)?.departureDate,
    // The latest date set before this flight, skipping empty ones: an emptied flight must not unbound the next.
    previousFlightDate: (state) => (legIndex: number) =>
      state.searchRequest.legs.slice(0, legIndex).findLast((leg) => leg.departureDate)?.departureDate,
    returnDate: (state) => {
      if (state.searchRequest.tripType === 'ONE_WAY') return;
      return state.searchRequest.legs.at(-1)?.departureDate;
    },
    committedReturnDate(): string | undefined {
      if (this.committedCriteriaKey.tripType === 'ONE_WAY') return;
      return this.committedCriteriaKey.legs.at(-1)?.departureDate;
    },

    originCode: (state) => stopCityCode(state.searchRequest.legs[0]?.from),
    committedOriginCode(): string | undefined {
      return stopCityCode(this.committedCriteriaKey.legs[0]?.from);
    },
    originCodeWithDefault(): string {
      return this.originCode ?? 'PAR';
    },
    fromCode: (state) => state.searchRequest.legs[0]?.from?.code,
    destinationCode: (state): string | undefined => stopCityCode(state.searchRequest.legs[0]?.to),
    committedDestinationCode(): string | undefined {
      return stopCityCode(this.committedCriteriaKey.legs[0]?.to);
    },
    legOriginCode: (state) => (legIndex: number) => stopCityCode(state.searchRequest.legs[legIndex]?.from),
    legDestinationCode: (state) => (legIndex: number) => stopCityCode(state.searchRequest.legs[legIndex]?.to),

    maxAdults: (state) => MAX_PAX - state.searchRequest.children - state.searchRequest.infants,
    maxChildren: (state) => MAX_PAX - state.searchRequest.adults - state.searchRequest.infants,
    maxInfants: (state) =>
      Math.min(state.searchRequest.adults, MAX_PAX - state.searchRequest.adults - state.searchRequest.children),
    areDatesSelected: (state) => {
      if (state.searchRequest.tripType === 'ONE_WAY') {
        return !!state.searchRequest.legs[0]?.departureDate;
      }
      if (state.searchRequest.tripType === 'ROUND_TRIP') {
        return !!state.searchRequest.legs[0]?.departureDate && !!state.searchRequest.legs[1]?.departureDate;
      }
      return state.searchRequest.legs.every((leg) => !!leg.departureDate);
    },
    areLocationsSelected: (state) => {
      if (state.searchRequest.tripType === 'MULTI_DEST') {
        return state.searchRequest.legs.every((leg) => !!(leg.from?.code && leg.to?.code));
      }
      const leg = state.searchRequest.legs[0];
      return !!(leg?.from?.code && leg?.to?.code);
    },
    hasRequiredCriteria(): boolean {
      return this.areDatesSelected && this.areLocationsSelected;
    },

    suggestionsKey:
      (state) =>
      (locale: string, searchText: string, context: SuggestionContext, excludeTrainStations?: boolean): string[] => [
        'suggestions',
        locale,
        searchText,
        buildSuggestionsExcludeCode(state.searchRequest.legs, context) ?? '',
        String(!!excludeTrainStations),
      ],

    toSuggestionsQueryString:
      (state) => (searchText: string, context: SuggestionContext, excludeTrainStations?: boolean) => {
        const parameters = new URLSearchParams({ query: searchText });
        const excludeCode = buildSuggestionsExcludeCode(state.searchRequest.legs, context);
        if (excludeCode) parameters.set('exclude', excludeCode);
        if (excludeTrainStations) parameters.set('excludeTrainStations', 'true');
        return parameters.toString();
      },

    criteriaQueryString() {
      const parameters = new URLSearchParams();
      if (!this.isRoundTrip) parameters.set(PARAM_TRIP_TYPE, this.searchRequest.tripType);
      if (this.searchRequest.cabinClass && this.searchRequest.cabinClass !== SEARCH_REQUEST_DEFAULTS.cabinClass)
        parameters.set(PARAM_CABIN_CLASS, this.searchRequest.cabinClass);
      if (this.searchRequest.adults !== SEARCH_REQUEST_DEFAULTS.adults)
        parameters.set(PARAM_ADULTS, String(this.searchRequest.adults));
      if (this.searchRequest.children !== SEARCH_REQUEST_DEFAULTS.children)
        parameters.set(PARAM_CHILDREN, String(this.searchRequest.children));
      if (this.searchRequest.infants !== SEARCH_REQUEST_DEFAULTS.infants)
        parameters.set(PARAM_INFANTS, String(this.searchRequest.infants));
      if (this.isOneWay) appendOneWayLeg(parameters, this.searchRequest.legs);
      else if (this.isRoundTrip) appendRoundTripLegs(parameters, this.searchRequest.legs);
      else appendMultiDestinationLegs(parameters, this.searchRequest.legs);
      return parameters.toString().toLowerCase();
    },
    mainCriteriaKey: (state): MainCriteriaKey => ({
      tripType: state.searchRequest.tripType,
      cabinClass: state.searchRequest.cabinClass,
      adults: state.searchRequest.adults,
      children: state.searchRequest.children,
      infants: state.searchRequest.infants,
      legs: state.searchRequest.legs.map((l) => ({
        from: l.from,
        to: l.to,
        departureDate: l.departureDate,
      })),
    }),
    committedCriteriaKey(): MainCriteriaKey {
      return this.searchedCriteriaKey ?? this.mainCriteriaKey;
    },
    hasSearched(): boolean {
      return !!this.searchedCriteriaKey;
    },
    searchRequested(): boolean {
      return this.active || this.fromDeepLink;
    },
    hasUncommittedCriteria(): boolean {
      if (!this.searchedCriteriaKey) return true;
      return toCacheKey([this.mainCriteriaKey]) !== toCacheKey([this.searchedCriteriaKey]);
    },
    committedPaxCount(): number {
      const key = this.committedCriteriaKey;
      return key.adults + key.children + key.infants;
    },
    committedLegCityCodes(): (string | undefined)[] {
      return this.committedCriteriaKey.legs.map((leg) => stopCityCode(leg.from));
    },
    committedLegArrivalCityCodes(): (string | undefined)[] {
      return this.committedCriteriaKey.legs.map((leg) => stopCityCode(leg.to));
    },
    // Every city the trip goes through, in order: a departure is dropped only when it is the city the
    // previous leg arrived in, so an open jaw (arriving in NYC, leaving from BOS) keeps both.
    committedItineraryStops(): SuggestionItem[] {
      return this.committedCriteriaKey.legs
        .flatMap((leg) => [leg.from, leg.to])
        .filter((stop, index, stops) => index === 0 || stopCityCode(stop) !== stopCityCode(stops[index - 1]));
    },
    committedItineraryCityCodes(): (string | undefined)[] {
      return this.committedItineraryStops.map((stop) => stopCityCode(stop));
    },
    committedExtraLegs(): number {
      return Math.max(0, this.committedCriteriaKey.legs.length - 2);
    },
  },
  actions: {
    applyFromUrlQuery(url: string, minDaysBeforeDeparture?: number) {
      const parameters = new URL(url, 'https://dummy.com').searchParams;
      const decoded = parseUrlQuery(parameters, this.tripTypeList, this.cabinClassList, this.suggestionItemTypeList);
      if (decoded.tripType) this.setTripType(decoded.tripType);
      if (decoded.cabinClass) this.searchRequest.cabinClass = decoded.cabinClass;
      // apply in decreasing order so constraints loosen before increasing values
      if (decoded.infants !== undefined) this.updateInfants(decoded.infants);
      if (decoded.children !== undefined) this.updateChildren(decoded.children);
      if (decoded.adults !== undefined) this.updateAdults(decoded.adults);
      if (decoded.legs) applyLegs(this.searchRequest.legs, decoded.legs.slice(0, MAX_LEGS));
      if (!this.isMultiDestination) return;
      // The store outlives a client-side navigation: flights of a previous, longer search must not ride along, not
      // even as the second flight the form always shows. A URL without any flight keeps them, like the other trip types.
      if (decoded.legs?.length) {
        this.searchRequest.legs.splice(decoded.legs.length);
        while (this.searchRequest.legs.length < 2) this.searchRequest.legs.push(newLeg());
      }
      clearUnsearchableFields(this.searchRequest.legs, minDaysBeforeDeparture);
    },
    snapshotCriteriaKey() {
      this.searchedCriteriaKey = this.mainCriteriaKey;
    },
    revertToCommittedCriteria() {
      if (!this.searchedCriteriaKey) return;
      const committed = this.searchedCriteriaKey;
      this.searchRequest.tripType = committed.tripType;
      this.searchRequest.cabinClass = committed.cabinClass;
      this.searchRequest.adults = committed.adults;
      this.searchRequest.children = committed.children;
      this.searchRequest.infants = committed.infants;
      this.searchRequest.legs = committed.legs.map(({ from, to, departureDate }) => {
        const leg = newLeg();
        leg.from = from;
        leg.to = to;
        leg.departureDate = departureDate;
        return leg;
      });
    },
    syncRoundTripLegs() {
      if (this.searchRequest.tripType !== 'ROUND_TRIP') return;
      const legA = this.searchRequest.legs[0];
      const legB = this.searchRequest.legs[1];
      if (!legA || !legB) return;
      legB.from = legA.to;
      legB.to = legA.from;
    },
    reverseLeg(legIndex: number) {
      const leg = this.searchRequest.legs[legIndex];
      if (!leg) return;
      const previousFrom = leg.from;
      leg.from = leg.to;
      leg.to = previousFrom;
      this.syncRoundTripLegs();
    },
    updateAdults(n: number) {
      const adults = Math.max(SEARCH_REQUEST_DEFAULTS.adults, Math.min(n, this.maxAdults));
      this.searchRequest.adults = adults;
      if (this.searchRequest.infants > adults) this.searchRequest.infants = adults;
    },
    updateChildren(n: number) {
      this.searchRequest.children = Math.max(SEARCH_REQUEST_DEFAULTS.children, Math.min(n, this.maxChildren));
    },
    updateInfants(n: number) {
      this.searchRequest.infants = Math.max(SEARCH_REQUEST_DEFAULTS.infants, Math.min(n, this.maxInfants));
    },
    // The request carries exactly the legs its trip type reads — one, two, or as many as the
    // multi-destination form holds — otherwise stale legs linger in the request, its cache key and
    // every per-leg filter. Coming back to a richer type recreates the missing blank legs.
    setTripType(tripType: TripType) {
      this.searchRequest.tripType = tripType;
      const legCount = tripType === 'ONE_WAY' ? 1 : 2;
      if (tripType !== 'MULTI_DEST') this.searchRequest.legs.splice(legCount);
      while (this.searchRequest.legs.length < legCount) this.searchRequest.legs.push(newLeg());
    },
    setReturnDate(date?: string) {
      const returnLeg = this.searchRequest.legs[1];
      if (returnLeg) returnLeg.departureDate = date ?? '';
    },
    setLegDepartureDate(legIndex: number, date?: string) {
      const leg = this.searchRequest.legs[legIndex];
      if (!leg) return;
      leg.departureDate = date ?? '';
      clearDatesBeforePreviousFlight(this.searchRequest.legs);
    },
    setLegDestination(legIndex: number, destination?: SuggestionItem) {
      const leg = this.searchRequest.legs[legIndex];
      if (!leg || !destination) return;
      leg.to = destination;
      const nextLeg = this.searchRequest.legs[legIndex + 1];
      // from?.: the origin field blurred without a suggestion leaves the next leg's origin undefined, not blank.
      // A copy, not the reference: editing the next leg's origin must not rewrite this destination.
      if (nextLeg && !nextLeg.from?.code) nextLeg.from = toPojo(destination);
    },
    addLeg() {
      if (!this.isMultiDestination || this.hasMaxLegs) return;
      const leg = newLeg();
      const previousDestination = this.searchRequest.legs.at(-1)?.to;
      // A copy, not the reference: editing the new leg's origin must not rewrite the previous destination.
      if (previousDestination) leg.from = toPojo(previousDestination);
      this.searchRequest.legs.push(leg);
    },
    removeLeg(index: number) {
      if (!this.isMultiDestination) return;
      this.searchRequest.legs.splice(index, 1);
      clearDatesBeforePreviousFlight(this.searchRequest.legs);
    },
  },
});

// Plain data, like the initial legs: a leg the URL adds on the server travels in the payload, which devalue
// refuses to serialise from a class instance ("Cannot stringify arbitrary non-POJOs" — TD-3).
function newLeg(): SearchLegRequest {
  const leg = new modelsImpl.SearchLegRequestImpl();
  leg.departureDate = '';
  leg.from.type = 'city';
  leg.to.type = 'city';
  return toPojo(leg);
}

function applyLegs(target: SearchLegRequest[], legs: Partial<SearchLegRequest>[]): void {
  for (const [legIndex, leg] of legs.entries()) {
    if (!target[legIndex]) target.push(newLeg());
    const targetLeg = target[legIndex];
    if (targetLeg) applyLegFields(targetLeg, leg);
  }
}

function applyLegFields(target: SearchLegRequest, leg: Partial<SearchLegRequest>): void {
  if (leg.from !== undefined) target.from = leg.from;
  if (leg.to !== undefined) target.to = leg.to;
  // An unreadable date clears the field rather than keeping the previous search's.
  if (leg.departureDate !== undefined) target.departureDate = DateTime.fromISO(leg.departureDate).toISODate() ?? '';
}

// A URL must not launch a search the multi-destination form would refuse: a date out of the search window or before
// the previous flight's, or a destination equal to the origin, is dropped, so the traveller picks it again.
function clearUnsearchableFields(legs: SearchLegRequest[], minDaysBeforeDeparture?: number): void {
  for (const leg of legs) {
    const isInWindow =
      isSearchDepartureDateAllowed(leg.departureDate, minDaysBeforeDeparture) &&
      isSearchDepartureDateNotTooFar(leg.departureDate);
    if (!isInWindow) leg.departureDate = '';
    if (leg.from?.code && leg.from.code === leg.to?.code) leg.to = newLeg().to;
  }
  clearDatesBeforePreviousFlight(legs);
}

// A date before any earlier flight's is dropped, whichever edit caused it (a moved date, a removed flight, a URL), so
// the traveller picks it again from a calendar bounded by that flight. The same day stays allowed, as in the form.
function clearDatesBeforePreviousFlight(legs: SearchLegRequest[]): void {
  let previousDate: DateTime | undefined;
  for (const leg of legs) {
    if (!leg.departureDate) continue;
    const date = DateTime.fromISO(leg.departureDate);
    if (previousDate && date < previousDate) leg.departureDate = '';
    else previousDate = date;
  }
}

function buildSuggestionsExcludeCode(legs: SearchLegRequest[], context: SuggestionContext): string | undefined {
  if (context.field !== 'to') return;
  return legs[context.legIndex]?.from?.code;
}

function parseUrlQuery(
  parameters: URLSearchParams,
  tripTypeList: TripType[],
  cabinClassList: CabinClass[],
  suggestionItemTypeList: SuggestionItemType[],
): Partial<Omit<SearchRequest, 'legs'>> & { legs?: Partial<SearchLegRequest>[] } {
  const result: Partial<Omit<SearchRequest, 'legs'>> & { legs?: Partial<SearchLegRequest>[] } = {};

  const tripType = tripTypeList.find((t) => t.toLowerCase() === parameters.get(PARAM_TRIP_TYPE));
  if (tripType) result.tripType = tripType;

  const cabinClass = cabinClassList.find((c) => c.toLowerCase() === parameters.get(PARAM_CABIN_CLASS));
  if (cabinClass) result.cabinClass = cabinClass;

  const adults = Number.parseInt(parameters.get(PARAM_ADULTS) ?? '', 10);
  if (!Number.isNaN(adults)) result.adults = adults;

  const children = Number.parseInt(parameters.get(PARAM_CHILDREN) ?? '', 10);
  if (!Number.isNaN(children)) result.children = children;

  const infants = Number.parseInt(parameters.get(PARAM_INFANTS) ?? '', 10);
  if (!Number.isNaN(infants)) result.infants = infants;

  const effectiveTripType = result.tripType ?? SEARCH_REQUEST_DEFAULTS.tripType;
  if (effectiveTripType === 'ONE_WAY') result.legs = parseOneWay(parameters, suggestionItemTypeList);
  else if (effectiveTripType === 'ROUND_TRIP') result.legs = parseRoundTrip(parameters, suggestionItemTypeList);
  else result.legs = parseMultiDestination(parameters, suggestionItemTypeList);

  return result;
}

function parseOneWay(
  parameters: URLSearchParams,
  suggestionItemTypeList: SuggestionItemType[],
): Partial<SearchLegRequest>[] {
  const fromCode = parseLocationIataCode(parameters.get(PARAM_LEGS_FROM_CODE));
  const fromType = suggestionItemTypeList.find((t) => useSnakeCase(t) === parameters.get(PARAM_LEGS_FROM_TYPE));
  const toCode = parseLocationIataCode(parameters.get(PARAM_LEGS_TO_CODE));
  const toType = suggestionItemTypeList.find((t) => useSnakeCase(t) === parameters.get(PARAM_LEGS_TO_TYPE));
  return [
    {
      from: fromCode && fromType ? { type: fromType, code: fromCode } : undefined,
      to: toCode && toType ? { type: toType, code: toCode } : undefined,
      departureDate: parameters.get(PARAM_LEGS_DATE) ?? undefined,
    },
  ];
}

function parseRoundTrip(
  parameters: URLSearchParams,
  suggestionItemTypeList: SuggestionItemType[],
): Partial<SearchLegRequest>[] {
  const fromCode = parseLocationIataCode(parameters.get(PARAM_LEGS_FROM_CODE));
  const fromType = suggestionItemTypeList.find((t) => useSnakeCase(t) === parameters.get(PARAM_LEGS_FROM_TYPE));
  const toCode = parseLocationIataCode(parameters.get(PARAM_LEGS_TO_CODE));
  const toType = suggestionItemTypeList.find((t) => useSnakeCase(t) === parameters.get(PARAM_LEGS_TO_TYPE));
  const dates = parameters.getAll(PARAM_LEGS_DATE);
  // always decode both legs to keep indexing — dates[0]=outbound, dates[1]=inbound
  return [
    {
      from: fromCode && fromType ? { type: fromType, code: fromCode } : undefined,
      to: toCode && toType ? { type: toType, code: toCode } : undefined,
      departureDate: dates.at(0),
    },
    { departureDate: dates.at(1) },
  ];
}

function parseMultiDestination(
  parameters: URLSearchParams,
  suggestionItemTypeList: SuggestionItemType[],
): Partial<SearchLegRequest>[] {
  const fromCodeByLeg = parameters.getAll(PARAM_LEGS_FROM_CODE);
  const fromTypeByLeg = parameters.getAll(PARAM_LEGS_FROM_TYPE);
  const toCodeByLeg = parameters.getAll(PARAM_LEGS_TO_CODE);
  const toTypeByLeg = parameters.getAll(PARAM_LEGS_TO_TYPE);
  const dateByLeg = parameters.getAll(PARAM_LEGS_DATE);
  const legCount = Math.max(fromCodeByLeg.length, toCodeByLeg.length, dateByLeg.length);
  const legs: Partial<SearchLegRequest>[] = [];
  // loop on max length so no leg is missed if arrays are unequal — decoder reads from/to/date by index
  for (let index = 0; index < legCount; index++) {
    const fromCode = parseLocationIataCode(fromCodeByLeg.at(index));
    const fromType = suggestionItemTypeList.find((t) => useSnakeCase(t) === fromTypeByLeg.at(index));
    const toCode = parseLocationIataCode(toCodeByLeg.at(index));
    const toType = suggestionItemTypeList.find((t) => useSnakeCase(t) === toTypeByLeg.at(index));
    legs.push({
      from: fromCode && fromType ? { type: fromType, code: fromCode } : undefined,
      to: toCode && toType ? { type: toType, code: toCode } : undefined,
      departureDate: dateByLeg.at(index),
    });
  }
  return legs;
}

function appendOneWayLeg(parameters: URLSearchParams, legs: SearchLegRequest[]): void {
  const leg = legs[0];
  if (!leg) return;
  if (leg.from.code && leg.from.type) {
    parameters.append(PARAM_LEGS_FROM_CODE, leg.from.code);
    parameters.append(PARAM_LEGS_FROM_TYPE, useSnakeCase(leg.from.type));
  }
  if (leg.to.code && leg.to.type) {
    parameters.append(PARAM_LEGS_TO_CODE, leg.to.code);
    parameters.append(PARAM_LEGS_TO_TYPE, useSnakeCase(leg.to.type));
  }
  const isoDate = DateTime.fromISO(leg.departureDate).toISODate();
  if (isoDate) parameters.append(PARAM_LEGS_DATE, isoDate);
}

function appendRoundTripLegs(parameters: URLSearchParams, legs: SearchLegRequest[]): void {
  const [origin, destination] = legs;
  if (origin?.from.code && origin.from.type) {
    parameters.append(PARAM_LEGS_FROM_CODE, origin.from.code);
    parameters.append(PARAM_LEGS_FROM_TYPE, useSnakeCase(origin.from.type));
  }
  if (origin?.to.code && origin.to.type) {
    parameters.append(PARAM_LEGS_TO_CODE, origin.to.code);
    parameters.append(PARAM_LEGS_TO_TYPE, useSnakeCase(origin.to.type));
  }
  // always append both dates even if empty to keep indexing — decoder reads dates[0]=outbound, dates[1]=inbound
  parameters.append(PARAM_LEGS_DATE, DateTime.fromISO(origin?.departureDate ?? '').toISODate() ?? '');
  parameters.append(PARAM_LEGS_DATE, DateTime.fromISO(destination?.departureDate ?? '').toISODate() ?? '');
}

function appendMultiDestinationLegs(parameters: URLSearchParams, legs: SearchLegRequest[]): void {
  // always append all arrays even if empty to keep indexing — decoder reads from/to/date by leg index
  for (const leg of legs) {
    parameters.append(PARAM_LEGS_FROM_CODE, leg.from.code ?? '');
    parameters.append(PARAM_LEGS_FROM_TYPE, useSnakeCase(leg.from.type ?? ''));
    parameters.append(PARAM_LEGS_TO_CODE, leg.to.code ?? '');
    parameters.append(PARAM_LEGS_TO_TYPE, useSnakeCase(leg.to.type ?? ''));
    parameters.append(PARAM_LEGS_DATE, DateTime.fromISO(leg.departureDate).toISODate() ?? '');
  }
}

function stopCityCode(stop?: SuggestionItem): string | undefined {
  return stop?.cityCode ?? stop?.code;
}
